import React from "react";
import { Doughnut, Bar } from "react-chartjs-2";
import { Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom"; // Import useNavigate for navigation
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
);

const TopicCompletionChart = ({ data, classroomId, studentId }) => {
  const navigate = useNavigate(); // Initialize navigate for navigation

  // Process data to get all topics
  const processData = () => {
    // Check if data exists and is an array
    if (!data || !Array.isArray(data)) {
      return { topTopics: [], others: [] }; // Return empty arrays if data is invalid
    }

    // Calculate combined percentage for each topic
    const topicsWithCombinedPercentage = data.map((topic) => ({
      ...topic,
      combinedPercentage: parseFloat(topic.percentage || 0),
    }));

    // Sort by combined percentage
    const sortedTopics = topicsWithCombinedPercentage.sort(
      (a, b) => b.combinedPercentage - a.combinedPercentage,
    );

    // Split into top 5 and others
    const topTopics = sortedTopics.slice(0, 5);
    const others = sortedTopics.slice(5);

    // Calculate total percentage for others
    const othersTotalPercentage = others.reduce(
      (sum, topic) => sum + topic.combinedPercentage,
      0,
    );

    // Create others category if there are remaining topics
    const othersCategory =
      others.length > 0
        ? [
            {
              name: "Others",
              combinedPercentage: othersTotalPercentage,
              isOthers: true,
              topics: others,
            },
          ]
        : [];

    return {
      topTopics,
      others: othersCategory,
    };
  };

  const { topTopics, others } = processData(); // Get processed data

  const handleViewDetails = () => {
    navigate(`/student/${classroomId}/topic-completion-details`, {
      state: {
        topics: [...topTopics, ...others],
        studentId: studentId,
      },
    });
  };

  const allTopics = [...topTopics, ...others];
  const chartLabels = allTopics.map(
    (topic) => `${topic.name} - ${topic.combinedPercentage.toFixed(1)}%`,
  );
  const chartData = allTopics.map((topic) => topic.combinedPercentage);

  // Doughnut chart data
  const doughnutData = {
    labels: chartLabels,
    datasets: [
      {
        data: chartData,
        backgroundColor: [
          "#564ffd", // Primary color
          "#ff6636", // Orange
          "#28a745", // Green
          "#17a2b8", // Cyan
          "#ffc107", // Yellow
          "#6c757d", // Grey for Others
        ],
        borderWidth: 0,
      },
    ],
  };

  // Bar chart data
  const barData = {
    labels: chartLabels,
    datasets: [
      {
        label: "Completion Percentage",
        data: chartData,
        backgroundColor: [
          "#564ffd", // Primary color
          "#ff6636", // Orange
          "#28a745", // Green
          "#17a2b8", // Cyan
          "#ffc107", // Yellow
          "#6c757d", // Grey for Others
        ],
        borderRadius: 8,
      },
    ],
  };

  const commonOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          padding: 20,
          usePointStyle: true,
          pointStyle: "circle",
          font: {
            size: 12,
          },
        },
      },
      // tooltip: {
      //   callbacks: {
      //     label: function (context) {
      //       const label = context.label || "";
      //       const value = context.raw; // Use context.raw instead of context.parsed for bar chart data
      //       return `${label.split(" - ")[0]}: ${
      //         value !== undefined ? value.toFixed(1) : 0
      //       }%`;
      //     },
      //   },
      // },
    },
  };

  const doughnutOptions = {
    ...commonOptions,
    cutout: "70%",
  };

  const barOptions = {
    ...commonOptions,
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: function (value) {
            return value + "%";
          },
        },
      },
    },
  };

  return (
    <div className="topic-completion-chart">
      <div className="charts-container">
        <div className="chart-wrapper">
          <div className="chart-container">
            <Doughnut data={doughnutData} options={doughnutOptions} />
          </div>
          <div className="chart-title">Topic Completion Distribution</div>
        </div>
        <div className="chart-wrapper">
          <div className="chart-container">
            <Bar data={barData} options={barOptions} />
          </div>
          <div className="chart-title">Topic Completion Progress</div>
        </div>
      </div>
      <div className="chart-actions">
        <Button
          variant="link"
          className="view-details-btn"
          onClick={handleViewDetails} // Update the button click handler
        >
          View All Details
        </Button>
      </div>
    </div>
  );
};

export default TopicCompletionChart;
